import React, { useState, useEffect } from "react";
import { Row, Col, Button } from "react-bootstrap";
import Select from "react-select";
import axiosClient from "../../../../config/axios.js";

const MapFilters = ({ values, onSearch, filters, setFilters, hideSearchButton = false }) => {
  const [craneTypes, setCraneTypes] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [clientTypes, setClientTypes] = useState([]);
  const [insurers, setInsurers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Obtener todas las opciones de los selects
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        // Obtener tipos de grúa
        const craneTypesResponse = await axiosClient.get(
          `/craneTypes/datatable`,
          {
            params: {
              start: 0,
              length: 1000, // Obtener todos los tipos
              search: "",
              sort: "crane_type_name",
              dir: "asc",
            },
          }
        );
        const craneTypesOptions = craneTypesResponse.data.data.map((item) => ({
          value: item.crane_type_name,
          label: item.crane_type_name,
        }));
        setCraneTypes(craneTypesOptions);

        // Obtener tipos de vehículo
        const vehicleTypesResponse = await axiosClient.get("/vehicleTypes/all");
        const vehicleTypesOptions = vehicleTypesResponse.data.data.map(
          (vehicleType) => ({
            value: vehicleType.id,
            label: vehicleType.vehicle_type_name,
          })
        );
        setVehicleTypes(vehicleTypesOptions);

        // Obtener tipos de cliente
        const clientTypesResponse = await axiosClient.get("/clientTypes/all");
        const clientTypesOptions = clientTypesResponse.data.data.map(
          (clientType) => ({
            value: clientType.id,
            label: clientType.client_type,
            client_type_code: clientType.client_type_code,
          })
        );
        setClientTypes(clientTypesOptions);

        // Obtener aseguradoras
        const insurersResponse = await axiosClient.get("/insurers/all");
        const insurersOptions = insurersResponse.data.data.map((insurance) => ({
          value: insurance.id,
          label: insurance.insurance_name,
        }));
        setInsurers(insurersOptions);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching filter options:", error);
        setLoading(false);
      }
    };

    fetchOptions();
  }, []);

  // Los filtros se inicializan en ModalOrdersMap, aquí solo los manejamos

  // Verificar si el tipo de cliente es "Aseguradora"
  const selectedClientType = clientTypes.find(
    (ct) => ct.value === filters?.clientTypeId?.value
  );
  const showInsuranceSelect =
    selectedClientType?.client_type_code === "ASEGURADORA";

  const colWidths = showInsuranceSelect
    ? hideSearchButton
      ? { crane: 3, vehicle: 3, client: 3, insurance: 3 }
      : { crane: 3, vehicle: 3, client: 3, insurance: 2, button: 1 }
    : hideSearchButton
      ? { crane: 4, vehicle: 4, client: 4 }
      : { crane: 4, vehicle: 3, client: 3, button: 2 };

  const handleCraneTypesChange = (selectedOptions) => {
    setFilters((prev) => ({
      ...prev,
      craneTypes: selectedOptions || [],
    }));
  };

  // Validar que se haya seleccionado al menos un tipo de grúa
  const hasCraneTypes = filters?.craneTypes && filters.craneTypes.length > 0;

  const handleVehicleTypeChange = (selectedOption) => {
    setFilters((prev) => ({
      ...prev,
      vehicleTypeId: selectedOption || null,
    }));
  };

  const handleClientTypeChange = (selectedOption) => {
    setFilters((prev) => ({
      ...prev,
      clientTypeId: selectedOption || null,
      // Si cambia el tipo de cliente y no es "Aseguradora", limpiar insurance
      insuranceId:
        selectedOption?.client_type_code === "ASEGURADORA"
          ? prev.insuranceId
          : null,
    }));
  };

  const handleInsuranceChange = (selectedOption) => {
    setFilters((prev) => ({
      ...prev,
      insuranceId: selectedOption || null,
    }));
  };

  const handleSearch = () => {
    // Validar que se haya seleccionado al menos un tipo de grúa
    if (!hasCraneTypes) {
      return; // No ejecutar búsqueda si no hay tipos de grúa seleccionados
    }
    if (onSearch) {
      onSearch(filters);
    }
  };

  if (loading) {
    return <div>Cargando filtros...</div>;
  }

  return (
    <Row className="mb-3 align-items-start">
      <Col md={colWidths.crane}>
        <label className="form-label">
          Tipo de Grúa <span className="text-danger">*</span>
        </label>
        <Select
          isMulti
          options={craneTypes}
          value={filters?.craneTypes || []}
          onChange={handleCraneTypesChange}
          placeholder="Seleccione tipos de grúa"
          isClearable
          styles={{
            control: (base, state) => ({
              ...base,
              borderColor: !hasCraneTypes ? '#dc3545' : base.borderColor,
              '&:hover': {
                borderColor: !hasCraneTypes ? '#dc3545' : base.borderColor,
              },
            }),
          }}
        />
        <div style={{ minHeight: '20px' }}>
          {!hasCraneTypes && (
            <small className="text-danger d-block mt-1">
              Debe seleccionar al menos un tipo de grúa
            </small>
          )}
        </div>
      </Col>
      <Col md={colWidths.vehicle}>
        <label className="form-label">Tipo de Vehículo</label>
        <Select
          options={vehicleTypes}
          value={filters?.vehicleTypeId || null}
          onChange={handleVehicleTypeChange}
          placeholder="Seleccione tipo de vehículo"
          isClearable
        />
        <div style={{ minHeight: '20px' }}></div>
      </Col>
      <Col md={colWidths.client}>
        <label className="form-label">Tipo de Cliente</label>
        <Select
          options={clientTypes}
          value={filters?.clientTypeId || null}
          onChange={handleClientTypeChange}
          placeholder="Seleccione tipo de cliente"
          isClearable
        />
        <div style={{ minHeight: '20px' }}></div>
      </Col>
      {showInsuranceSelect && (
        <Col md={colWidths.insurance}>
          <label className="form-label">Aseguradora</label>
          <Select
            options={insurers}
            value={filters?.insuranceId || null}
            onChange={handleInsuranceChange}
            placeholder="Seleccione aseguradora"
            isClearable
          />
          <div style={{ minHeight: '20px' }}></div>
        </Col>
      )}
      {!hideSearchButton && (
        <Col
          md={colWidths.button}
          className="d-flex justify-content-end align-items-start"
        >
          <Button
            variant="primary"
            onClick={handleSearch}
            disabled={!hasCraneTypes}
            style={{ height: "38px", marginTop: "28px" }}
          >
            Buscar
          </Button>
        </Col>
      )}
    </Row>
  );
};

export default MapFilters;
